/**
 * LiteYouTube Component
 * 
 * Lightweight YouTube embed that loads the player only when clicked
 * Saves ~700KB+ of initial load and improves LCP significantly
 * 
 * Usage:
 * <LiteYouTube videoId="PO0CI6RmmyA" title="Video Title" />
 */
import React, { useState } from 'react';

const LiteYouTube = ({ 
  videoId, 
  title = 'YouTube video',
  thumbnailQuality = 'maxresdefault', // 'default', 'hqdefault', 'mqdefault', 'sddefault', 'maxresdefault'
  className = '',
  aspectRatio = '16/9' // Can be '16/9', '4/3', etc.
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleClick = () => {
    setIsLoaded(true);
  };

  const thumbnailUrl = imageError 
    ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` // Fallback to hqdefault
    : `https://i.ytimg.com/vi/${videoId}/${thumbnailQuality}.jpg`;

  return (
    <div 
      className={`relative w-full ${className}`}
      style={{ aspectRatio }}
    >
      {!isLoaded ? (
        <div
          onClick={handleClick}
          className="relative w-full h-full cursor-pointer group"
          role="button"
          aria-label={`Play video: ${title}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
        >
          {/* Thumbnail */}
          <img
            src={thumbnailUrl}
            alt={title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
          
          {/* Dark overlay on hover */}
          <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-200" />
          
          {/* Play button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              {/* Red YouTube button background */}
              <div className="absolute inset-0 bg-red-600 opacity-80 group-hover:opacity-100 transition-opacity duration-200" 
                   style={{ width: '68px', height: '48px', borderRadius: '10px' }} />
              
              {/* Play triangle */}
              <svg 
                className="relative z-10" 
                width="68" 
                height="48" 
                viewBox="0 0 68 48"
                aria-hidden="true"
              >
                <path 
                  d="M 24,12 L 24,36 L 44,24 Z" 
                  fill="white"
                  className="group-hover:scale-105 transform transition-transform duration-200"
                  style={{ transformOrigin: 'center' }}
                />
              </svg>
            </div>
          </div>

          {/* Video duration badge (optional) */}
          {/* You can add video duration if you have it */}
        </div>
      ) : (
        // Load actual YouTube iframe when clicked
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      )}
    </div>
  );
};

export default LiteYouTube;
